import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { executionPlanApi } from '../services/api';
import api from '../services/api';
import DevicePicker from '../components/DevicePicker';
import PremiumBadge from '../components/PremiumBadge';
import GoalSearch from '../components/GoalSearch';
import { useWorkspace } from '../context/WorkspaceContext';
import './ExecutionPlanEdit.css';

/**
 * ExecutionPlanEdit Page
 * 
 * Dedicated page for editing execution plans
 */
function ExecutionPlanEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentWorkspace } = useWorkspace();
    const realDevicesEnabled = currentWorkspace?.subscription?.quotas?.realDevicesEnabled === true;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Plan data
    const [plan, setPlan] = useState({
        name: '',
        description: '',
        executionMode: 'sequential',
        stopOnFailure: true,
        retryFailedGoals: false,
        maxRetries: 2,
        platform: 'android',
        appId: null,
        deviceProvider: 'aws',
        deviceArn: 'auto'
    });

    const [goals, setGoals] = useState([]);
    const [apps, setApps] = useState([]);
    const [showDevicePicker, setShowDevicePicker] = useState(false);
    const [showGoalSearch, setShowGoalSearch] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState(null);

    useEffect(() => {
        if (plan.platform === 'android' && !realDevicesEnabled) {
            setSelectedDevice({
                arn: 'emulator',
                name: 'Android Emulator (Standard)',
                model: 'Virtual Emulator',
                os: 'Android 14 (Standard)',
                platform: 'android',
                availability: 'AVAILABLE',
                provider: 'emulator'
            });
            setPlan(prev => ({
                ...prev,
                deviceProvider: 'emulator',
                deviceArn: 'emulator'
            }));
        }
    }, [realDevicesEnabled, plan.platform]);

    // Auto-scroll logic
    const lastGoalRef = useRef(null);
    const prevGoalsLength = useRef(goals.length);

    useEffect(() => {
        if (goals.length > prevGoalsLength.current) {
            // New goals added, scroll to the last one
            setTimeout(() => {
                lastGoalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
        }
        prevGoalsLength.current = goals.length;
    }, [goals.length]);

    useEffect(() => {
        loadPlan();
        loadApps();
    }, [id]);

    const loadPlan = async () => {
        try {
            const response = await executionPlanApi.getExecutionPlan(id);
            const planData = response.data.executionPlan || response.data;

            setPlan({
                name: planData.name,
                description: planData.description || '',
                executionMode: planData.executionMode,
                stopOnFailure: planData.stopOnFailure,
                retryFailedGoals: planData.retryFailedGoals,
                maxRetries: planData.maxRetries,
                platform: planData.platform,
                appId: planData.appId,
                deviceProvider: planData.deviceProvider,
                deviceArn: planData.deviceArn
            });

            // Transform goals for editing
            const transformedGoals = (planData.testGoals || [])
                .sort((a, b) => (a.ExecutionPlanGoals?.order || 0) - (b.ExecutionPlanGoals?.order || 0))
                .map(goal => ({
                    id: goal.id,
                    goalText: goal.goalText,
                    priority: goal.priority,
                    order: goal.ExecutionPlanGoals?.order || 0,
                    isEnabled: goal.ExecutionPlanGoals?.isEnabled !== undefined
                        ? goal.ExecutionPlanGoals.isEnabled
                        : true
                }));

            setGoals(transformedGoals);
        } catch (err) {
            console.error('Failed to load plan:', err);
            setError('Failed to load execution plan');
        } finally {
            setLoading(false);
        }
    };

    const loadApps = async () => {
        try {
            const workspaceId = localStorage.getItem('workspaceId');
            const response = await api.get('/apps', {
                params: { workspaceId }
            });
            setApps(response.data.rows || []);
        } catch (err) {
            console.error('Failed to load apps:', err);
        }
    };

    const handleSave = async () => {
        // Validation
        const enabledGoals = goals.filter(g => g.isEnabled);
        if (enabledGoals.length === 0) {
            setError('At least one goal must be enabled');
            return;
        }

        if (!plan.name.trim()) {
            setError('Plan name is required');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            await executionPlanApi.updateExecutionPlan(id, {
                ...plan,
                goals: goals.map((goal, index) => ({
                    ...goal,
                    order: index + 1 // Ensure sequential order
                }))
            });

            navigate(`/execution-plans/${id}`);
        } catch (err) {
            console.error('Failed to save plan:', err);
            setError(err.response?.data?.error || 'Failed to save execution plan');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        navigate(`/execution-plans/${id}`);
    };

    const handleAddGoal = () => {
        setGoals([...goals, {
            goalText: '',
            priority: 'medium',
            order: goals.length + 1,
            isEnabled: true
        }]);
    };

    const handleAddExistingGoals = (selectedGoals) => {
        const newGoals = selectedGoals.map((goal, index) => ({
            id: goal.id,
            goalText: goal.goalText,
            priority: goal.priority || 'medium',
            order: goals.length + index + 1,
            isEnabled: true
        }));
        setGoals([...goals, ...newGoals]);
        setShowGoalSearch(false);
    };

    const handleUpdateGoal = (index, field, value) => {
        const updated = [...goals];
        updated[index] = { ...updated[index], [field]: value };
        setGoals(updated);
    };

    const handleDeleteGoal = (index) => {
        if (window.confirm('Are you sure you want to delete this goal?')) {
            setGoals(goals.filter((_, i) => i !== index));
        }
    };

    const handleMoveGoal = (index, direction) => {
        if (
            (direction === 'up' && index === 0) ||
            (direction === 'down' && index === goals.length - 1)
        ) {
            return;
        }

        const newIndex = direction === 'up' ? index - 1 : index + 1;
        const updated = [...goals];
        [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
        setGoals(updated);
    };

    const handleDeviceSelect = (device) => {
        setSelectedDevice(device);
        setPlan({
            ...plan,
            deviceProvider: device.provider,
            deviceArn: device.arn
        });
        setShowDevicePicker(false);
    };

    if (loading) {
        return (
            <div className="execution-plan-edit">
                <div className="execution-plan-edit__loading">
                    <div className="execution-plan-edit__spinner"></div>
                    <p>Loading execution plan...</p>
                </div>
            </div>
        );
    }

    if (error && !plan.name) {
        return (
            <div className="execution-plan-edit">
                <div className="execution-plan-edit__error">
                    <h2>Error</h2>
                    <p>{error}</p>
                    <button onClick={() => navigate('/execution-plans')}>
                        ← Back to Execution Plans
                    </button>
                </div>
            </div>
        );
    }

    const selectedApp = apps.find(app => app.id === plan.appId);

    return (
        <div className="execution-plan-edit">
            {/* Header */}
            <div className="execution-plan-edit__header">
                <h1>Edit Execution Plan</h1>
                <div className="execution-plan-edit__actions">
                    <button
                        className="execution-plan-edit__btn execution-plan-edit__btn--secondary"
                        onClick={handleCancel}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        className="execution-plan-edit__btn execution-plan-edit__btn--primary"
                        onClick={handleSave}
                        disabled={saving || (plan.platform === 'ios' && !realDevicesEnabled)}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>

            {plan.platform === 'ios' && !realDevicesEnabled && (
                <div className="execution-plan-edit__error-banner" style={{ backgroundColor: '#fff5f5', color: '#e53e3e', border: '1px solid #fed7d7' }}>
                    ⚠️ iOS execution is a premium feature. Please <Link to="/settings/billing" style={{ color: '#e53e3e', textDecoration: 'underline', fontWeight: 'bold' }}>Upgrade to Business or Enterprise</Link> to save or run tests on iOS.
                </div>
            )}

            {error && (
                <div className="execution-plan-edit__error-banner">
                    {error}
                </div>
            )}

            {/* Basic Info */}
            <div className="execution-plan-edit__section">
                <h2>Basic Information</h2>
                <div className="execution-plan-edit__form">
                    <div className="execution-plan-edit__form-group">
                        <label>Plan Name *</label>
                        <input
                            type="text"
                            value={plan.name}
                            onChange={(e) => setPlan({ ...plan, name: e.target.value })}
                            placeholder="Enter plan name"
                        />
                    </div>
                    <div className="execution-plan-edit__form-group">
                        <label>Description</label>
                        <textarea
                            value={plan.description}
                            onChange={(e) => setPlan({ ...plan, description: e.target.value })}
                            placeholder="Enter plan description (optional)"
                            rows={3}
                        />
                    </div>
                </div>
            </div>

            {/* Configuration */}
            <div className="execution-plan-edit__section">
                <h2>Configuration</h2>
                <div className="execution-plan-edit__form">
                    <div className="execution-plan-edit__form-row">
                        <div className="execution-plan-edit__form-group">
                            <label>Execution Mode</label>
                            <select
                                value={plan.executionMode}
                                onChange={(e) => setPlan({ ...plan, executionMode: e.target.value })}
                            >
                                <option value="sequential">Sequential</option>
                                <option value="parallel">Parallel</option>
                            </select>
                        </div>
                        <div className="execution-plan-edit__form-group">
                            <label>Platform</label>
                            <select
                                value={plan.platform}
                                onChange={(e) => setPlan({ ...plan, platform: e.target.value })}
                            >
                                <option value="android">Android</option>
                                <option value="ios" disabled={!realDevicesEnabled}>iOS {!realDevicesEnabled ? '(Premium)' : ''}</option>
                            </select>
                        </div>
                    </div>

                    <div className="execution-plan-edit__form-group">
                        <label>App</label>
                        <select
                            value={plan.appId || ''}
                            onChange={(e) => setPlan({ ...plan, appId: e.target.value || null })}
                        >
                            <option value="">Select an app</option>
                            {apps
                                .filter(app => app.platform === plan.platform)
                                .map(app => (
                                    <option key={app.id} value={app.id}>
                                        {app.name} ({app.version})
                                    </option>
                                ))}
                        </select>
                    </div>

                    <div className="execution-plan-edit__form-group">
                        <label>Device</label>
                        <div className="execution-plan-edit__device-selector">
                            <input
                                type="text"
                                value={selectedDevice?.name || plan.deviceArn || 'Auto (pick best device)'}
                                readOnly
                            />
                            {!realDevicesEnabled && <PremiumBadge title='Upgrade to run tests on real devices' />}
                            <button
                                type="button"
                                onClick={() => setShowDevicePicker(true)}
                                className="execution-plan-edit__btn execution-plan-edit__btn--secondary"
                                disabled={plan.platform === 'android' && !realDevicesEnabled}
                                title={(plan.platform === 'android' && !realDevicesEnabled) ? 'Standard emulator selected. Upgrade to access real devices.' : ''}
                            >
                                Choose Device
                            </button>
                        </div>
                        {!realDevicesEnabled && plan.platform === 'android' && (
                            <div className="emulator-warning-info" style={{ marginTop: '5px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                ℹ️ Your tests will run on Android Emulator. <Link to="/settings/billing" style={{ color: 'var(--primary-color)', textDecoration: 'underline', fontWeight: 'bold' }}>Upgrade to Business or Enterprise</Link> to run tests on real devices.
                            </div>
                        )}
                    </div>

                    <div className="execution-plan-edit__checkboxes">
                        <label className="execution-plan-edit__checkbox">
                            <input
                                type="checkbox"
                                checked={plan.stopOnFailure}
                                onChange={(e) => setPlan({ ...plan, stopOnFailure: e.target.checked })}
                            />
                            <span>Stop execution on first failure</span>
                        </label>

                        <label className="execution-plan-edit__checkbox">
                            <input
                                type="checkbox"
                                checked={plan.retryFailedGoals}
                                onChange={(e) => setPlan({ ...plan, retryFailedGoals: e.target.checked })}
                            />
                            <span>Retry failed goals</span>
                        </label>
                    </div>

                    {plan.retryFailedGoals && (
                        <div className="execution-plan-edit__form-group">
                            <label>Max Retries</label>
                            <input
                                type="number"
                                min="1"
                                max="5"
                                value={plan.maxRetries}
                                onChange={(e) => setPlan({ ...plan, maxRetries: parseInt(e.target.value) })}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Goals */}
            <div className="execution-plan-edit__section">
                <div className="execution-plan-edit__section-header">
                    <h2>Test Goals ({goals.length})</h2>
                    <div className="execution-plan-edit__actions">
                        <button
                            className="execution-plan-edit__btn execution-plan-edit__btn--secondary"
                            onClick={() => setShowGoalSearch(true)}
                        >
                            + Add Existing
                        </button>
                        <button
                            className="execution-plan-edit__btn execution-plan-edit__btn--primary"
                            onClick={handleAddGoal}
                        >
                            + Add Goal
                        </button>
                    </div>
                </div>

                <div className="execution-plan-edit__goals">
                    {goals.map((goal, index) => (
                        <div
                            key={index}
                            className="execution-plan-edit__goal"
                            ref={index === goals.length - 1 ? lastGoalRef : null}
                        >
                            <div className="execution-plan-edit__goal-header">
                                <span className="execution-plan-edit__goal-number">#{index + 1}</span>
                                <div className="execution-plan-edit__goal-controls">
                                    <button
                                        onClick={() => handleMoveGoal(index, 'up')}
                                        disabled={index === 0}
                                        title="Move up"
                                    >
                                        ↑
                                    </button>
                                    <button
                                        onClick={() => handleMoveGoal(index, 'down')}
                                        disabled={index === goals.length - 1}
                                        title="Move down"
                                    >
                                        ↓
                                    </button>
                                    <button
                                        onClick={() => handleDeleteGoal(index)}
                                        className="execution-plan-edit__goal-delete"
                                        title="Delete goal"
                                    >
                                        🗑️
                                    </button>
                                </div>
                            </div>

                            <div className="execution-plan-edit__goal-content">
                                <textarea
                                    value={goal.goalText}
                                    onChange={(e) => handleUpdateGoal(index, 'goalText', e.target.value)}
                                    placeholder="Enter goal description"
                                    rows={2}
                                />

                                <div className="execution-plan-edit__goal-meta">
                                    <select
                                        value={goal.priority}
                                        onChange={(e) => handleUpdateGoal(index, 'priority', e.target.value)}
                                    >
                                        <option value="low">Low Priority</option>
                                        <option value="medium">Medium Priority</option>
                                        <option value="high">High Priority</option>
                                    </select>

                                    <label className="execution-plan-edit__checkbox">
                                        <input
                                            type="checkbox"
                                            checked={goal.isEnabled}
                                            onChange={(e) => handleUpdateGoal(index, 'isEnabled', e.target.checked)}
                                        />
                                        <span>Enabled</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    ))}

                    {goals.length === 0 && (
                        <div className="execution-plan-edit__empty">
                            <p>No goals added yet. Click "Add Goal" to get started.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Device Picker Modal */}
            {showDevicePicker && (
                <div className="execution-plan-edit__modal-overlay" onClick={() => setShowDevicePicker(false)}>
                    <div className="execution-plan-edit__modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="execution-plan-edit__modal-header">
                            <h2>Select Device</h2>
                            <button
                                className="execution-plan-edit__modal-close"
                                onClick={() => setShowDevicePicker(false)}
                            >
                                ✕
                            </button>
                        </div>
                        <DevicePicker
                            platform={plan.platform}
                            onDeviceSelect={handleDeviceSelect}
                        />
                    </div>
                </div>
            )}

            {/* Goal Search Modal */}
            {showGoalSearch && (
                <div className="execution-plan-edit__modal-overlay" onClick={() => setShowGoalSearch(false)}>
                    <div className="execution-plan-edit__modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="execution-plan-edit__modal-header">
                            <h2>Add Existing Goals</h2>
                            <button
                                className="execution-plan-edit__modal-close"
                                onClick={() => setShowGoalSearch(false)}
                            >
                                ✕
                            </button>
                        </div>
                        <GoalSearch
                            platform={plan.platform}
                            existingGoalIds={goals.map(g => g.id).filter(Boolean)}
                            onSelect={handleAddExistingGoals}
                            onCancel={() => setShowGoalSearch(false)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default ExecutionPlanEdit;
